// =============================================================================
//  Global axios interceptor — install once at app boot from src/index.tsx.
//
//  Purpose
//  -------
//  Catch HTTP 401 responses from the BSM API, clear the auth cookie, and
//  bounce the user to the login page.  Without this, a stale JWT (e.g. after
//  a server-side password rotation or a long-lived expired token) produces
//  a silently-empty dashboard because every loader saga sees 401 and writes
//  an error notice instead of populating the store.
//
//  Skipped
//  -------
//  - Auth endpoints themselves (`/users/sign_in`, `/users/password`, etc.) —
//    those return 401 on bad credentials and the LoginPage component already
//    surfaces that via the saga's error notice.
//  - Requests issued while the user is already on /login — avoids a redirect
//    loop when an expired JWT triggers on the same page that handles login.
// =============================================================================

import axios from 'axios';

import { removeAuthTokenCookie } from './auth.util';

const AUTH_ENDPOINT_PATTERNS = [/\/users\/sign_in/, /\/users\/password/, /\/users\/confirmation/];

const isAuthEndpoint = (url: string | undefined): boolean => {
  if (!url) return false;
  return AUTH_ENDPOINT_PATTERNS.some((re) => re.test(url));
};

let installed = false;

export const installAxiosInterceptors = (): void => {
  if (installed) return;
  installed = true;

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const url = error?.config?.url as string | undefined;

      if (status === 401 && !isAuthEndpoint(url)) {
        // Strip the dead cookie so we don't keep sending it.
        removeAuthTokenCookie();
        // Avoid a redirect loop if the user is already on the login screen
        // (e.g. they're on /login and the LoginPage itself fires a 401).
        const onLogin = window.location.pathname.startsWith('/login');
        if (!onLogin) {
          // Use full navigation rather than react-router's navigate() so we
          // don't have to thread the navigator through every saga.  The auth
          // saga always sends users to /dashboard after a successful login,
          // so we don't try to remember the original path here.
          window.location.replace('/login');
        }
      }

      return Promise.reject(error);
    }
  );
};
